import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";

import { ExpandableContent, MarkdownArea } from "common/components";
import { StyledExpandButton } from "common/components/ExpandableText";
import { useTranslate } from "common/utilities/useTranslate";
import { MachineTranslationLabel } from "domains/topicDetail/components/MachineTranslationLabel";
import { TopicDetailCuration } from "domains/topicDetail/components/TopicDetailCuration";
import { TopicDetailParentTopic } from "domains/topicDetail/components/TopicDetailParentTopic";
import type { Topic } from "entities/topics/types";

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledMarkdownText = styled(MarkdownArea)`
  margin: 0;
  font-size: 0.9rem;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.textFade};
`;

type TopicDescriptionProps = { description?: string; keyIssue?: Topic; selectedTopic?: Topic; topic: Topic };

export const TopicDescription = ({ selectedTopic, topic, keyIssue }: TopicDescriptionProps) => {
  const translate = useTranslate();
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const parentTopicRef = useRef<HTMLDivElement>(null);
  const [collapsedHeight, setCollapsedHeight] = useState<number | undefined>(0);

  const textLimit = 4;
  const description = selectedTopic?.description;

  useEffect(() => {
    let textHeightLimit = 0;
    if (textRef.current?.children[0]) {
      const height = textRef.current.offsetHeight;
      const lineHeightCSS = getComputedStyle(textRef.current.children[0]).lineHeight;
      const lineHeight = parseInt(lineHeightCSS.substring(0, lineHeightCSS.length - 2));
      const noOfLines = Math.ceil(height / (lineHeight || 1));
      const textLinesLimit = Math.min(noOfLines, textLimit);
      textHeightLimit = textLinesLimit * lineHeight;
    }
    if (textHeightLimit > 0) {
      setCollapsedHeight(textHeightLimit);
      return;
    }

    if (keyIssue?.parent_topic && parentTopicRef.current) {
      const height = parentTopicRef.current.offsetHeight;
      setCollapsedHeight(height);
      return;
    }
    setCollapsedHeight(undefined);
  }, [description, keyIssue?.parent_topic]);

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  return (
    <>
      <ExpandableContent
        collapsedHeight={collapsedHeight !== undefined ? `${collapsedHeight}px` : undefined}
        isActive={collapsedHeight === undefined || isExpanded}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div ref={textRef} onClick={toggleExpansion}>
          {description && <StyledMarkdownText>{description}</StyledMarkdownText>}
        </div>
        <div ref={parentTopicRef}>
          {keyIssue?.parent_topic !== undefined && keyIssue.parent_topic !== null && (
            <TopicDetailParentTopic parent_topic={keyIssue.parent_topic} />
          )}
        </div>
        <TopicDetailCuration topic={topic} keyIssue={keyIssue} />
      </ExpandableContent>
      <StyledRow>
        {collapsedHeight !== undefined && (
          <StyledExpandButton data-test-id="expand-button" onClick={toggleExpansion}>
            {translate(!isExpanded ? "app.read_more" : "app.read_less")}
          </StyledExpandButton>
        )}
        <MachineTranslationLabel topic={topic} keyIssue={keyIssue} />
      </StyledRow>
    </>
  );
};
